/**
 * Où placer la bulle ou le panneau autour du personnage, pour qu'ils restent
 * ENTIÈREMENT dans l'écran — à gauche, à droite, en haut et en bas.
 *
 * ## Pourquoi un calcul, et pas du CSS
 *
 * Le CSS les accrochait au personnage (`right: 78px; bottom: 10px`) : ils
 * poussaient vers le haut et vers la gauche, quelle que soit la place
 * disponible. Or le personnage bouge — le guidage l'emmène près de la section
 * montrée, le visiteur le glisse où il veut. Dès qu'il approchait d'un bord,
 * la bulle ou le panneau sortait de l'écran (capture du 2026-09-14 : bulle
 * coupée par le haut de la fenêtre).
 *
 * Et le code qui plaçait le personnage raisonnait sur une bulle SOUS lui, d'une
 * hauteur estimée en dur — deux fois faux.
 *
 * ## Ce que garantit ce calcul
 *
 * Pour un contenu qui tient en largeur dans l'écran (le CSS y veille par ses
 * `max-width`), le rectangle rendu est dans l'écran, à `MARGE_ECRAN` des bords.
 * Un contenu plus haut que l'écran reçoit une hauteur maximale : il défile, il
 * n'est jamais coupé.
 *
 * Aucun accès au DOM : c'est ce qui le rend testable, et c'est le seul endroit
 * du correctif qui puisse être prouvé automatiquement.
 */

export type Cote = 'gauche' | 'droite';

/** Un rectangle en coordonnées d'écran (celles de `getBoundingClientRect`). */
export type Boite = { left: number; top: number; largeur: number; hauteur: number };

export type Taille = { largeur: number; hauteur: number };

export type Placement = {
  /** Coordonnées d'écran du coin haut-gauche du contenu. */
  left: number;
  top: number;
  /** Côté du personnage où le contenu a fini par tenir. */
  cote: Cote;
  /** Renseignée seulement si le contenu est plus haut que l'écran : il doit défiler. */
  maxHauteur: number | null;
};

/** Distance minimale entre le contenu et chaque bord de l'écran. */
export const MARGE_ECRAN = 8;

/**
 * Écart horizontal entre le contenu et le personnage. Reprend le rendu d'avant :
 * `right: 78px` sur un conteneur de 72 px.
 */
export const ECART_PERSONNAGE = 6;

export function placerAupresDuPersonnage(
  perso: Boite,
  contenu: Taille,
  ecran: Taille,
  options: {
    /** Côté essayé en premier. À gauche par défaut, comme avant. */
    cote?: Cote;
    /** Remontée du bas du contenu par rapport au bas du personnage (10 px pour la bulle). */
    decalageBas?: number;
  } = {}
): Placement {
  const cotePrefere = options.cote ?? 'gauche';
  const decalageBas = options.decalageBas ?? 0;

  const hauteurDisponible = Math.max(0, ecran.hauteur - 2 * MARGE_ECRAN);
  const tropHaut = contenu.hauteur > hauteurDisponible;
  const hauteur = tropHaut ? hauteurDisponible : contenu.hauteur;

  const leftDuCote = (cote: Cote) =>
    cote === 'gauche'
      ? perso.left - ECART_PERSONNAGE - contenu.largeur
      : perso.left + perso.largeur + ECART_PERSONNAGE;
  const tient = (left: number) =>
    left >= MARGE_ECRAN && left + contenu.largeur <= ecran.largeur - MARGE_ECRAN;

  let cote: Cote = cotePrefere;
  if (!tient(leftDuCote(cotePrefere))) {
    // Le côté habituel déborde : on prend le côté le plus dégagé. Si l'autre côté a
    // la place, c'est forcément lui — personnage collé à un bord. Sinon (écran
    // étroit), le recadrage ci-dessous fait tenir le contenu, quitte à passer
    // devant le personnage : mieux vaut le masquer que couper le texte.
    const placeAGauche = perso.left - ECART_PERSONNAGE - MARGE_ECRAN;
    const placeADroite = ecran.largeur - MARGE_ECRAN - (perso.left + perso.largeur + ECART_PERSONNAGE);
    cote = placeAGauche >= placeADroite ? 'gauche' : 'droite';
  }
  const left = borner(leftDuCote(cote), MARGE_ECRAN, ecran.largeur - MARGE_ECRAN - contenu.largeur);

  // Aligné sur le bas du personnage et poussant vers le haut, comme avant — puis
  // ramené dans l'écran : près du haut, le contenu descend au lieu de sortir.
  const top = borner(
    perso.top + perso.hauteur - decalageBas - hauteur,
    MARGE_ECRAN,
    ecran.hauteur - MARGE_ECRAN - hauteur
  );

  return { left, top, cote, maxHauteur: tropHaut ? hauteurDisponible : null };
}

/**
 * Ramène `valeur` entre `min` et `max`. Si l'intervalle est vide (contenu plus
 * grand que la place), le bord de départ l'emporte : le début reste visible.
 */
function borner(valeur: number, min: number, max: number): number {
  if (max < min) return min;
  return Math.min(max, Math.max(min, valeur));
}
